import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";

/**
 * Idle Screen Tracker (client daemon).
 *
 * Runs in the background whenever the user is:
 *   - authenticated,
 *   - has the `use_idle_tracker` capability, and
 *   - is currently checked-in on Attendance (only_when_checked_in=true).
 *
 * Detects idle time via mouse/keyboard/visibility events. Periodically uploads
 * screenshots via `getDisplayMedia` if the operator enabled screenshot capture
 * globally. The initial screen-share prompt is triggered from the Attendance
 * check-in flow through the exposed `window.__idleTracker.requestScreen()` API;
 * this component only *uses* the stream that was already granted.
 */

const IDLE_STREAM_KEY = "__idleScreenStream";

export function getIdleStream() {
  if (typeof window === "undefined") return null;
  return window[IDLE_STREAM_KEY] || null;
}

export async function requestIdleStream() {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getDisplayMedia) {
    throw new Error("Your browser does not support screen sharing.");
  }
  const existing = getIdleStream();
  if (existing && existing.getVideoTracks?.().some(t => t.readyState === "live")) {
    return existing;
  }
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: 1, width: { max: 1920 }, height: { max: 1080 } },
    audio: false,
  });
  window[IDLE_STREAM_KEY] = stream;
  return stream;
}

export function releaseIdleStream() {
  const s = getIdleStream();
  if (s) {
    try { s.getTracks().forEach(t => t.stop()); } catch (e) { /* ignore */ }
    delete window[IDLE_STREAM_KEY];
  }
}

export default function IdleTracker() {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const [cfg, setCfg] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [screenActive, setScreenActive] = useState(false);
  const lastActivityAt = useRef(Date.now());
  const idleStartedAt = useRef(null);
  const heartbeatRef = useRef(null);
  const idleCheckRef = useRef(null);
  const screenshotRef = useRef(null);
  const attendanceRef = useRef(null);

  const enabled = user && can("use_idle_tracker") && cfg?.enabled;

  // ---- 1. Load config on mount, refresh every 5 min ----
  useEffect(() => {
    if (!user) return;
    let cancel = false;
    const load = async () => {
      try {
        const r = await api.get("/idle-tracker/config");
        if (!cancel) setCfg(r.data);
      } catch (e) { /* ignore */ }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancel = true; clearInterval(t); };
  }, [user]);

  // ---- 2. Poll attendance status every 30s ----
  useEffect(() => {
    if (!enabled) { setIsChecked(false); return; }
    let cancel = false;
    const check = async () => {
      try {
        const r = await api.get("/attendance/today");
        if (!cancel) setIsChecked(!!r.data.is_checked_in);
      } catch (e) {
        if (!cancel) setIsChecked(false);
      }
    };
    check();
    attendanceRef.current = setInterval(check, 30 * 1000);
    return () => {
      cancel = true;
      if (attendanceRef.current) clearInterval(attendanceRef.current);
    };
  }, [enabled]);

  const active = enabled && (cfg?.only_when_checked_in ? isChecked : true);

  // ---- 3. Post an event to backend ----
  const postEvent = useCallback(async (type, extra = {}) => {
    if (!enabled) return;
    try {
      await api.post("/idle-tracker/events", {
        type,
        page: typeof window !== "undefined" ? window.location.pathname : "",
        ...extra,
      });
    } catch (e) { /* ignore silently — this is background noise */ }
  }, [enabled]);

  // ---- 4. Activity detection ----
  useEffect(() => {
    if (!active) return;
    const on = () => {
      const wasIdle = idleStartedAt.current !== null;
      lastActivityAt.current = Date.now();
      if (wasIdle) {
        const started = idleStartedAt.current;
        const dur = Math.round((Date.now() - started) / 1000);
        idleStartedAt.current = null;
        postEvent("idle_end", {
          started_at: new Date(started).toISOString(),
          ended_at: new Date().toISOString(),
          duration_seconds: dur,
        });
      }
    };
    const events = ["mousemove", "mousedown", "keydown", "wheel", "touchstart"];
    events.forEach(e => window.addEventListener(e, on, { passive: true }));
    const onVis = () => {
      if (document.visibilityState === "visible") on();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      events.forEach(e => window.removeEventListener(e, on));
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [active, postEvent]);

  // ---- 5. Idle detection loop ----
  useEffect(() => {
    if (!active) {
      // if user checks out, close any open idle interval
      if (idleStartedAt.current) {
        const started = idleStartedAt.current;
        const dur = Math.round((Date.now() - started) / 1000);
        idleStartedAt.current = null;
        postEvent("idle_end", {
          started_at: new Date(started).toISOString(),
          ended_at: new Date().toISOString(),
          duration_seconds: dur,
        });
      }
      return;
    }
    const threshold = (cfg?.idle_threshold_seconds || 300) * 1000;
    idleCheckRef.current = setInterval(() => {
      const sinceLast = Date.now() - lastActivityAt.current;
      if (sinceLast > threshold && idleStartedAt.current === null) {
        idleStartedAt.current = lastActivityAt.current;
        postEvent("idle_start", {
          started_at: new Date(idleStartedAt.current).toISOString(),
        });
      }
    }, 10 * 1000);
    return () => {
      if (idleCheckRef.current) clearInterval(idleCheckRef.current);
    };
  }, [active, cfg?.idle_threshold_seconds, postEvent]);

  // ---- 6. Heartbeat ----
  useEffect(() => {
    if (!active) return;
    const interval = (cfg?.heartbeat_interval_seconds || 60) * 1000;
    heartbeatRef.current = setInterval(() => {
      postEvent("heartbeat");
    }, interval);
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [active, cfg?.heartbeat_interval_seconds, postEvent]);

  // ---- 7. Screenshot loop ----
  const captureScreenshot = useCallback(async (reason = "interval") => {
    if (!active || !cfg?.screenshot_enabled) return;
    const stream = getIdleStream();
    if (!stream) return;
    const track = stream.getVideoTracks()[0];
    if (!track || track.readyState !== "live") return;
    try {
      // Use video element to capture frame
      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      // Wait one frame
      await new Promise(r => setTimeout(r, 200));
      const w = video.videoWidth || 1280;
      const h = video.videoHeight || 720;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, w, h);
      video.pause();
      video.srcObject = null;
      const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.72));
      if (!blob) return;
      const fd = new FormData();
      fd.append("file", blob, `screenshot-${Date.now()}.jpg`);
      fd.append("reason", reason);
      fd.append("page", window.location.pathname);
      await api.post("/idle-tracker/screenshots", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (e) { /* ignore */ }
  }, [active, cfg?.screenshot_enabled]);

  // Watch stream for changes + set screenActive
  useEffect(() => {
    if (!active || !cfg?.screenshot_enabled) { setScreenActive(false); return; }
    const check = () => {
      const s = getIdleStream();
      const on = !!(s && s.getVideoTracks?.().some(t => t.readyState === "live"));
      setScreenActive(on);
      if (s && on) {
        s.getVideoTracks().forEach(t => {
          if (!t.__idleWired) {
            t.__idleWired = true;
            t.addEventListener("ended", () => {
              setScreenActive(false);
              postEvent("screen_stopped");
            });
          }
        });
      }
    };
    check();
    const t = setInterval(check, 5000);
    return () => clearInterval(t);
  }, [active, cfg?.screenshot_enabled, postEvent]);

  // Periodic screenshot loop
  useEffect(() => {
    if (!active || !cfg?.screenshot_enabled || !screenActive) return;
    const interval = (cfg?.screenshot_interval_minutes || 15) * 60 * 1000;
    // Snap first shortly after start (10s) to prove it works, then on schedule
    const initial = setTimeout(() => captureScreenshot("interval"), 10 * 1000);
    screenshotRef.current = setInterval(() => captureScreenshot("interval"), interval);
    return () => {
      clearTimeout(initial);
      if (screenshotRef.current) clearInterval(screenshotRef.current);
    };
  }, [active, cfg?.screenshot_enabled, cfg?.screenshot_interval_minutes, screenActive, captureScreenshot]);

  // ---- 8. Persistent visible banner while tracking ----
  if (!active) return null;

  return (
    <div
      className="fixed bottom-3 left-3 z-40 border border-border bg-white shadow-md px-3 py-2 text-xs flex items-center gap-2"
      data-testid="idle-tracker-banner"
    >
      <span className={`inline-block w-2 h-2 rounded-full ${screenActive || !cfg?.screenshot_enabled ? "bg-green-500" : "bg-orange-500"}`} />
      <div>
        <div className="font-semibold uppercase font-mono-mini text-[10px]">
          {cfg?.screenshot_enabled
            ? (screenActive ? "Activity + screen recording" : "Activity tracking (screen paused)")
            : "Activity tracking"}
        </div>
        <div className="text-muted-foreground text-[10px]">
          Your idle time is monitored while you are checked in.
        </div>
      </div>
    </div>
  );
}
